/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer, useLocalStore } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { BasicModal, BasicModalProps } from 'src/core/components/BasicModal';
import { LuisDropdown } from 'src/core/components/LuisDropdown';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { useResponsiveMode } from 'src/core/hooks/useResponsiveMode';
import { AzureBlobContainerStore } from 'src/core/stores/AzureBlobContainerStore';
import { AzureBlobStore } from 'src/core/stores/AzureBlobStore';
import { AzureStorageAccountStore } from 'src/core/stores/AzureStorageAccountStore';
import { getStylist } from 'src/core/themes/luisStylist';
import { withModalMounter } from 'src/core/utils/hocs/withModalMounter';
import { createResourceStoreHouse } from 'src/luis/core/stores/resourceStoreHouse';
import { ResourceStoreHouse } from 'src/luis/types/stores/core/ResourceStoreHouse';
import { TranslationBlobStoreTable } from 'src/translation/components/TranslationBlobStoreTable';

type AzureContainerBrowsingModalProps = {
    useRemoteContainer?: boolean;
    selectedStorageAccount: string;
    isStorageAccountsLoading: boolean;
    storageAccounts: AzureStorageAccountStore[];
    onStorageAccountChange: (storageAccount: string) => void;
    showSelectAllCheckBox?: boolean;
} & BasicModalProps<{ files: AzureBlobStore[]; isAllFiles: boolean; selectedContainer: string }>;

const messages = defineMessages({
    add: { id: 'languageStudio.azureContainerBrowsingModal.add', defaultMessage: 'Add' },
    title: { id: 'languageStudio.azureContainerBrowsingModal.title', defaultMessage: 'Browse blob storage' },
    fileNameHeader: { id: 'languageStudio.azureContainerBrowsingModal.fileNameHeader', defaultMessage: 'File name' },
    fileTypeHeader: { id: 'languageStudio.azureContainerBrowsingModal.fileTypeHeader', defaultMessage: 'File type' },
    lastModifiedHeader: { id: 'languageStudio.azureContainerBrowsingModal.lastModifiedHeader', defaultMessage: 'Last modified' },
    blobContainerLabel: { id: 'languageStudio.azureContainerBrowsingModal.blobContainerLabel', defaultMessage: 'Blob container' },
    storageAccountLabel: { id: 'languageStudio.azureContainerBrowsingModal.storageAccountLabel', defaultMessage: 'Storage account' },
    note: { id: 'languageStudio.azureContainerBrowsingModal.note', defaultMessage: 'Select source files from your Azure blob storage.' },
    selectAllFilesLabel: {
        id: 'languageStudio.azureContainerBrowsingModal.selectAllFilesLabel',
        defaultMessage: 'Select all files in container'
    },
    selectStorageAccountPlaceholder: {
        id: 'languageStudio.azureContainerBrowsingModal.selectStorageAccountPlaceholder',
        defaultMessage: 'Select a blob storage account'
    },
    selectBlobContainerPlaceholder: {
        id: 'languageStudio.azureContainerBrowsingModal.selectBlobContainerPlaceholder',
        defaultMessage: 'Select a blob container'
    }
});

const { styleDiv } = getStylist('AzureContainerBrowsingModal');
const ChildIcon = styleDiv('childIconDiv', theme => ({
    width: '12px',
    height: '24px',
    borderLeft: `1px solid ${theme.borderColor}`,
    borderBottom: `1px solid ${theme.borderColor}`
}));

export const AzureContainerBrowsingModal = withModalMounter(
    observer((props: AzureContainerBrowsingModalProps) => {
        const {
            onSubmit,
            storageAccounts,
            selectedStorageAccount,
            onStorageAccountChange,
            isStorageAccountsLoading,
            useRemoteContainer = true,
            showSelectAllCheckBox = true,
            ...rest
        } = props;
        const filesTracker = useProgressTracker();
        const { localize } = useLocalization();
        const containersTracker = useProgressTracker();
        const { dispatcher, rootStore } = useGlobalState();
        const { isLargeOrGreater } = useResponsiveMode();
        const filesStoreHouse = useLocalStore<ResourceStoreHouse<AzureBlobStore>>(createResourceStoreHouse);
        const azureContainersStoreHouse = useLocalStore<ResourceStoreHouse<AzureBlobContainerStore>>(createResourceStoreHouse);
        const dropdownsMinWidth = isLargeOrGreater ? '300px' : '';

        const [selectedContainer, setSelectedContainer] = React.useState('');

        const [selectedBlobFiles, setSelectedBlobFiles] = React.useState<AzureBlobStore[]>([]);
        const [isAllFilesSelected, setIsAllFilesSelected] = React.useState<boolean>(
            selectedBlobFiles.length === filesStoreHouse.items.length && !!selectedBlobFiles.length
        );

        const { subscriptionId } = rootStore.azureStore.session;
        const selectedContainerStore = azureContainersStoreHouse.findById(selectedContainer);
        const selectedStorageAccountStore = storageAccounts.find(s => s.name === selectedStorageAccount);
        const storageAccountsOptions: IDropdownOption[] = storageAccounts.map(c => ({ key: c.name, text: c.name }));
        const containersOptions: IDropdownOption[] = azureContainersStoreHouse.items.map(c => ({ key: c.id, text: c.name }));

        React.useEffect(() => {
            if (selectedStorageAccount) {
                dispatcher.dispatch('getAzureContainersByStorageAccountName', {
                    toastData: {},
                    subscriptionId,
                    tracker: containersTracker,
                    name: selectedStorageAccount,
                    localStore: azureContainersStoreHouse,
                    resourceGroupName: selectedStorageAccountStore?.resourceGroupName
                });
            }
        }, [selectedStorageAccount]);

        React.useEffect(() => {
            if (selectedContainer) {
                dispatcher.dispatch('getAzureBlobsByContainerName', {
                    toastData: {},
                    tracker: filesTracker,
                    localStore: filesStoreHouse,
                    storageAccountName: selectedStorageAccount,
                    containerName: selectedContainerStore.name
                });
            }
        }, [selectedContainer]);

        return (
            <BasicModal
                {...rest}
                doneButtonText={localize(messages.add)}
                dialogContentProps={{ title: localize(messages.title), subText: localize(messages.note) }}
                valid={Boolean(selectedBlobFiles.length) || isAllFilesSelected || (useRemoteContainer && Boolean(selectedContainer))}
                onSubmit={() =>
                    onSubmit({
                        isAllFiles: selectedBlobFiles.length === filesStoreHouse.items.length && !!selectedBlobFiles.length,
                        selectedContainer: selectedContainerStore.name,
                        files: selectedBlobFiles
                    })
                }>
                <Stack tokens={{ padding: '20px 0' }}>
                    <Stack horizontal={isLargeOrGreater} horizontalAlign="space-between">
                        <Label required>{localize(messages.storageAccountLabel)}</Label>

                        <LuisDropdown
                            options={storageAccountsOptions}
                            selectedKey={selectedStorageAccount}
                            isLoading={isStorageAccountsLoading}
                            styles={{ dropdown: { minWidth: dropdownsMinWidth } }}
                            data-automation-id="azureStorageAccountDropdown"
                            placeholder={localize(messages.selectStorageAccountPlaceholder)}
                            onChange={(_, option) => onStorageAccountChange(option.key as string)}
                            calloutProps={{ styles: { calloutMain: { maxHeight: `${dropdownsMinWidth} !important` } } }}
                            disabled={!onStorageAccountChange}
                        />
                    </Stack>

                    <Stack horizontal tokens={{ childrenGap: 8, padding: '0 0 0 32px' }}>
                        <ChildIcon />
                        <Stack grow horizontal={isLargeOrGreater} horizontalAlign="space-between" tokens={{ padding: '10px 0 0 0' }}>
                            <Label required>{localize(messages.blobContainerLabel)}</Label>

                            <Stack tokens={{ childrenGap: 10 }}>
                                <LuisDropdown
                                    options={containersOptions}
                                    selectedKey={selectedContainer}
                                    disabled={!selectedStorageAccount}
                                    data-automation-id="blobContainerDropdown"
                                    styles={{ dropdown: { minWidth: dropdownsMinWidth } }}
                                    placeholder={localize(messages.selectBlobContainerPlaceholder)}
                                    onChange={(_, option) => setSelectedContainer(option.key as string)}
                                    calloutProps={{ styles: { calloutMain: { maxHeight: `${dropdownsMinWidth} !important` } } }}
                                    isLoading={containersTracker.isRunning || azureContainersStoreHouse.tracker.isRunning}
                                />

                                {!useRemoteContainer && showSelectAllCheckBox && (
                                    <Checkbox
                                        checked={isAllFilesSelected}
                                        label={localize(messages.selectAllFilesLabel)}
                                        onChange={(_, checked) => {
                                            setIsAllFilesSelected(checked);
                                            setSelectedBlobFiles(checked ? filesStoreHouse.items : []);
                                        }}
                                        disabled={!selectedContainer || filesTracker.isRunning}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    </Stack>

                    {!useRemoteContainer && [
                        <TranslationBlobStoreTable
                            disableSearch
                            files={filesStoreHouse.items}
                            selectedContainer={selectedContainerStore?.name}
                            isFilesLoading={filesStoreHouse.tracker.isRunning}
                            isAllFilesSelected={isAllFilesSelected}
                            selectedBlobFiles={selectedBlobFiles}
                            hiddenColumnKeys={['downloadButton']}
                            onChangeIsAllFilesSelected={setIsAllFilesSelected}
                            onChangeSelectedBlobFiles={setSelectedBlobFiles}
                        />
                    ]}
                </Stack>
            </BasicModal>
        );
    })
);
